import { useState } from "react";
import { format } from "date-fns";
import { Inbox as InboxIcon, Send } from "lucide-react";

const timeLabel = (iso) => {
  try {
    return format(new Date(iso), "MMM d, h:mm a");
  } catch {
    return "";
  }
};

const preview = (t) => {
  const last = t.messages?.[t.messages.length - 1];
  if (!last) return "";
  return `${last.sender === "ari" ? "You: " : ""}${last.body.slice(0, 60)}${last.body.length > 60 ? "…" : ""}`;
};

const AdminInbox = ({ threads, onReply, onToggleRead }) => {
  const [selected, setSelected] = useState(0);
  const [reply, setReply] = useState("");
  const [sending, setSending] = useState(false);

  const thread = threads[selected];

  const send = async (e) => {
    e.preventDefault();
    if (!thread || !reply.trim()) return;
    setSending(true);
    try {
      await onReply(thread.id, reply.trim());
      setReply("");
    } finally {
      setSending(false);
    }
  };

  if (threads.length === 0) {
    return (
      <div className="mt-8 flex flex-col items-center gap-4 rounded-3xl border border-line bg-white px-6 py-20 text-center" data-testid="admin-inbox-empty">
        <span className="flex h-14 w-14 items-center justify-center rounded-full bg-sand">
          <InboxIcon className="h-6 w-6 text-marmalade" />
        </span>
        <p className="font-display text-2xl text-ink">No messages yet</p>
        <p className="max-w-xs text-sm text-inkSoft">
          When pet owners write from the site, their messages land here for a private reply.
        </p>
      </div>
    );
  }

  return (
    <div className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-12">
      <div className="space-y-3 lg:col-span-5">
        {threads.map((t, i) => (
          <button
            key={t.id}
            onClick={() => {
              setSelected(i);
              if (t.unread) onToggleRead(t.id);
            }}
            data-testid={`admin-inbox-item-${t.id}`}
            className={`w-full rounded-3xl border p-5 text-left transition-[border-color,background-color] duration-200 ${
              selected === i ? "border-marmalade bg-white shadow-[0_10px_30px_rgba(217,108,74,0.12)]" : "border-line bg-white hover:border-ink/25"
            }`}
          >
            <div className="flex items-center justify-between gap-3">
              <span className="font-semibold text-ink">{t.owner_name}</span>
              {t.unread && (
                <span className="rounded-full bg-marmalade px-2.5 py-1 font-mono text-[9px] uppercase tracking-[0.16em] text-white" data-testid="inbox-unread-chip">
                  New
                </span>
              )}
            </div>
            <p className="mt-1 text-sm text-inkSoft">
              {t.subject}
              {t.booking_ref ? ` · ${t.booking_ref}` : ""}
            </p>
            <p className="mt-2 truncate text-sm text-inkSoft">{preview(t)}</p>
            <p className="mt-2 font-mono text-[9px] uppercase tracking-[0.16em] text-inkSoft">
              {timeLabel(t.updated_at)}
            </p>
          </button>
        ))}
      </div>

      {thread && (
        <div className="flex h-fit flex-col rounded-3xl border border-line bg-white p-6 md:p-8 lg:col-span-7" data-testid="admin-thread-view">
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-line pb-4">
            <div>
              <p className="font-display text-2xl font-medium text-ink">{thread.subject}</p>
              <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.18em] text-inkSoft">
                {thread.owner_name} · {thread.owner_email}
                {thread.booking_ref ? ` · ${thread.booking_ref}` : ""}
              </p>
            </div>
            <button
              onClick={() => onToggleRead(thread.id)}
              data-testid="admin-inbox-toggle-read"
              className="rounded-full border border-line px-4 py-2 font-mono text-[9px] uppercase tracking-[0.16em] text-inkSoft transition-colors duration-200 hover:bg-sand"
            >
              Mark as {thread.unread ? "read" : "unread"}
            </button>
          </div>
          <div className="mt-5 max-h-[420px] flex-1 space-y-4 overflow-y-auto pr-1">
            {thread.messages.map((m) => (
              <div key={m.id} className={`flex ${m.sender === "ari" ? "justify-end" : "justify-start"}`}>
                <div
                  className={`max-w-[80%] rounded-2xl px-5 py-3.5 text-sm leading-relaxed ${
                    m.sender === "ari" ? "bg-forest text-cream" : "bg-sand text-ink"
                  }`}
                  data-testid={m.sender === "ari" ? "admin-ari-bubble" : "admin-owner-bubble"}
                >
                  <p>{m.body}</p>
                  <p className={`mt-2 font-mono text-[9px] uppercase tracking-[0.16em] ${m.sender === "ari" ? "text-cream/60" : "text-inkSoft"}`}>
                    {m.sender === "ari" ? "Ari" : thread.owner_name} · {timeLabel(m.created_at)}
                  </p>
                </div>
              </div>
            ))}
          </div>
          <form onSubmit={send} className="mt-5 flex gap-3 border-t border-line pt-5">
            <input
              value={reply}
              onChange={(e) => setReply(e.target.value)}
              placeholder={`Reply to ${thread.owner_name}…`}
              data-testid="admin-inbox-reply-input"
              className="w-full flex-1 rounded-2xl border border-line bg-cream px-5 py-4 text-base text-ink placeholder:text-inkSoft/50 transition-[border-color] duration-200 focus:border-marmalade focus:outline-none"
            />
            <button
              type="submit"
              disabled={sending || !reply.trim()}
              data-testid="admin-inbox-reply-send"
              className="inline-flex items-center gap-2 rounded-full bg-marmalade px-6 py-3.5 font-mono text-[10px] uppercase tracking-[0.2em] text-white transition-[background-color] duration-300 hover:bg-marmalade-deep disabled:opacity-50"
            >
              <Send className="h-3.5 w-3.5" />
              Send
            </button>
          </form>
        </div>
      )}
    </div>
  );
};

export default AdminInbox;
